export interface OrderItem {
    id: string;
    createdAt: number | string;
    checked?: boolean;
    pendingSync?: boolean;
    pendingDelete?: boolean;
}

const itemTimestamp = <T extends OrderItem>(item: T): number => {
    const value = typeof item.createdAt === 'number'
        ? item.createdAt : Date.parse(item.createdAt);
    return Number.isFinite(value) ? value : 0;
};

export function normalizeItemOrder<T extends OrderItem>(
    items: readonly T[],
    savedOrder?: readonly string[],
): T[] {
    const byId = new Map<string, T>();
    items.forEach((item): void => {
        if (typeof item.id === 'string' && item.id.length > 0 && !byId.has(item.id)) {
            byId.set(item.id, item);
        }
    });
    const saved = [...new Set(savedOrder ?? [])].filter((id): boolean => byId.has(id));
    const savedIds = new Set(saved);
    const added = [...byId.values()].filter((item): boolean => !savedIds.has(item.id));
    added.sort((a, b): number => itemTimestamp(b) - itemTimestamp(a));
    return [...added, ...saved.map((id): T => byId.get(id)!)];
}

export function projectItemGroups<T extends OrderItem>(items: readonly T[], grouped: boolean): T[] {
    if (!grouped) return [...items];
    return [
        ...items.filter((item): boolean => !item.checked),
        ...items.filter((item): boolean => Boolean(item.checked)),
    ];
}

export function applyItemMove<T extends OrderItem>(
    baseItems: readonly T[],
    sourceId: string,
    destinationIndex: number,
    grouped: boolean,
): string[] {
    const source = baseItems.find((item): boolean => item.id === sourceId);
    const unchanged = baseItems.map((item): string => item.id);
    if (!source) return unchanged;
    const group = baseItems.filter((item): boolean =>
        !grouped || Boolean(item.checked) === Boolean(source.checked));
    const from = group.findIndex((item): boolean => item.id === sourceId);
    if (!Number.isInteger(destinationIndex)
        || destinationIndex < 0 || destinationIndex >= group.length) {
        return unchanged;
    }
    const moved = group.map((item): string => item.id);
    const [id] = moved.splice(from, 1);
    moved.splice(destinationIndex, 0, id);
    let index = 0;
    return baseItems.map((item): string =>
        !grouped || Boolean(item.checked) === Boolean(source.checked)
            ? moved[index++]
            : item.id);
}

export function hasPendingOrderItems(items: readonly OrderItem[]): boolean {
    return items.some((item): boolean => typeof item.id === 'string' && item.id.startsWith('temp-')
        || Boolean(item.pendingSync) || Boolean(item.pendingDelete));
}
